import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, MapPin, Factory, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';

const IndustrialEPCCornwall = () => {
  const towns = ['Truro', 'Newquay', 'Bodmin', 'Camborne', 'Redruth', 'Falmouth', 'Penzance', 'St Austell', 'Launceston', 'Bude'];
  const industrialEstates = [
    { name: 'Threemilestone Industrial Estate', location: 'Truro', desc: 'Major trading estate serving mid-Cornwall businesses' },
    { name: 'Indian Queens Industrial Estate', location: 'St Columb', desc: 'Central Cornwall location with good A30 access' },
    { name: 'Pool Innovation Centre', location: 'Redruth', desc: 'Modern business and light industrial park' },
    { name: 'Bodmin Business Park', location: 'Bodmin', desc: 'Growing industrial area near the A30 corridor' },
    { name: 'Cardrew Industrial Estate', location: 'Redruth', desc: 'Established industrial estate with mixed unit sizes' }
  ];

  const localSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Industrial EPC Assessments Cornwall",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Performance Direct",
      "areaServed": { "@type": "State", "name": "Cornwall" }
    },
    "areaServed": { "@type": "State", "name": "Cornwall" },
    "serviceType": "Industrial Energy Performance Certificate"
  };

  return (
    <>
      <Helmet>
        <title>Industrial EPC Cornwall | Warehouse & Factory EPCs Truro, Newquay</title>
        <meta name="description" content="Industrial EPC assessments across Cornwall including Truro, Newquay, Bodmin and Camborne. Professional warehouse and factory EPC surveys for Cornish industrial properties." />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/industrial-epc-cornwall" />
        <script type="application/ld+json">{JSON.stringify(localSchema)}</script>
      </Helmet>
      
      <Navbar />
      <Breadcrumbs items={[{ label: 'Industrial EPC', path: '/industrial-epc' }, { label: 'Cornwall', path: '/industrial-epc-cornwall' }]} />
      
      <section className="bg-gradient-to-br from-cyan-800 via-cyan-700 to-cyan-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-cyan-500/20 text-cyan-200 px-4 py-2 rounded-full text-sm mb-6">
              <MapPin className="w-4 h-4" /> Cornwall
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Industrial EPC Assessments in Cornwall</h1>
            <p className="text-xl text-cyan-200 mb-8">Professional warehouse, factory and workshop EPC surveys across Truro, Newquay, Bodmin and throughout Cornwall.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-cyan-700 hover:bg-gray-100">
                <Link to="/booking?service=commercial_warehouse">Get Cornwall EPC Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href="tel:01392980077"><Phone className="mr-2 w-5 h-5" />Call 01392 980077</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Industrial EPC Services Across Cornwall</h2>
            <p className="text-gray-700">
              Cornwall's industrial sector encompasses marine engineering, food processing, renewable energy manufacturing and traditional trades. Energy Performance Direct provides professional <Link to="/industrial-epc" className="text-cyan-600 hover:underline">industrial EPC assessments</Link> throughout the county, supporting landlords and business owners who need compliant energy certificates for their commercial and industrial properties.
            </p>
            <p className="text-gray-700">
              Our accredited non-domestic energy assessors cover all major Cornish towns including Truro, Newquay, Bodmin, Camborne, Redruth, Falmouth, Penzance, St Austell, Launceston and Bude. We regularly assess industrial properties on established estates along the A30 corridor and throughout coastal industrial areas.
            </p>
            <p className="text-gray-700">
              Cornwall's geography means many industrial buildings face unique challenges including coastal exposure, older construction methods and limited infrastructure for certain heating systems. Our assessors understand these local factors and provide accurate SBEM-calculated certificates with practical improvement recommendations suited to Cornish conditions.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Cornwall Industrial Areas We Cover</h2>
            <div className="not-prose grid md:grid-cols-2 gap-4">
              {industrialEstates.map((estate, i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900">{estate.name}</h3>
                  <p className="text-sm text-cyan-600 mb-2">{estate.location}</p>
                  <p className="text-sm text-gray-600">{estate.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">Towns Covered in Cornwall</h2>
            <p className="text-gray-700">We provide industrial EPC assessments throughout Cornwall including:</p>
            <div className="not-prose flex flex-wrap gap-2 mt-4">
              {towns.map((town, i) => (
                <span key={i} className="px-3 py-1 bg-cyan-100 text-cyan-800 rounded-full text-sm">{town}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Link to="/industrial-epc" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Factory className="w-6 h-6 text-cyan-600 mb-2" />
                <h3 className="font-semibold">Industrial EPCs</h3>
                <p className="text-sm text-gray-600">All industrial building types</p>
              </Link>
              <Link to="/warehouse-epc" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Building2 className="w-6 h-6 text-cyan-600 mb-2" />
                <h3 className="font-semibold">Warehouse EPCs</h3>
                <p className="text-sm text-gray-600">Distribution and storage</p>
              </Link>
              <Link to="/services/commercial_warehouse" className="p-4 bg-white rounded-lg hover:shadow-md transition-shadow">
                <Building2 className="w-6 h-6 text-cyan-600 mb-2" />
                <h3 className="font-semibold">Commercial EPCs</h3>
                <p className="text-sm text-gray-600">All commercial buildings</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Your Cornwall Industrial EPC Quote</h2>
          <p className="text-xl text-green-100 mb-8">Professional assessments throughout Cornwall.</p>
          <Button asChild size="lg" className="bg-white text-green-600 hover:bg-gray-100">
            <Link to="/booking?service=commercial_warehouse">Request Quote <ArrowRight className="ml-2 w-5 h-5" /></Link>
          </Button>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default IndustrialEPCCornwall;